/**
 * CopilotBriefingWidget — Iter-369 admin dashboard card.
 *
 * Renders today's Copilot Morning Briefing findings: critical issues at
 * the top, action labels below each card. Shows an empty state with a
 * "Run scan now" button (calls the manual scheduler trigger) when no
 * briefing exists yet.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, ShieldAlert, Info, CheckCircle2, RefreshCw, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import api, { getErrorMessage } from '@/lib/api';

const SEVERITY_META = {
  critical: { color: 'bg-rose-100 text-rose-800 border-rose-300', icon: ShieldAlert },
  high:     { color: 'bg-orange-100 text-orange-800 border-orange-300', icon: AlertTriangle },
  medium:   { color: 'bg-amber-100 text-amber-800 border-amber-300', icon: AlertTriangle },
  low:      { color: 'bg-slate-100 text-slate-700 border-slate-300', icon: Info },
};

export const CopilotBriefingWidget = ({ tenantId }) => {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['copilot-briefing', tenantId],
    queryFn: () => api.get('/copilot/morning-briefing', { params: { tenant_id: tenantId }})
      .then(r => r.data),
    enabled: !!tenantId,
    refetchInterval: 60000,
  });
  // iter-371 — show a small "hours saved (30d)" pill alongside the title
  const { data: learnings } = useQuery({
    queryKey: ['copilot-learnings', tenantId],
    queryFn: () => api.get('/copilot/learnings', { params: { tenant_id: tenantId, days: 30 }})
      .then(r => r.data),
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
  });
  const runMut = useMutation({
    mutationFn: () => api.post(`/school/tenants/${tenantId}/scheduler/run/copilot_morning_briefing`)
      .then(r => r.data),
    onSuccess: () => {
      toast.success('Scan complete');
      qc.invalidateQueries({ queryKey: ['copilot-briefing', tenantId] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const findings = data?.findings || [];
  const summary = data?.summary || {};

  return (
    <Card data-testid="copilot-briefing-widget">
      <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldAlert className="h-4 w-4 text-violet-600" /> Ecosystem Health
            {summary.critical > 0 && (
              <Badge className="bg-rose-600 text-white text-[10px]">{summary.critical} CRITICAL</Badge>
            )}
            {learnings?.estimated_hours_saved > 0 && (
              <Badge className="bg-violet-100 text-violet-700 text-[10px]" data-testid="copilot-hours-saved-badge">
                ~{learnings.estimated_hours_saved}h saved · 30d
              </Badge>
            )}
          </CardTitle>
          <CardDescription className="text-xs">
            {data?.ran_at ? `Last scanned ${new Date(data.ran_at).toLocaleString()}` : 'No scan yet today'}
            {' · '}
            <span className="text-rose-600">{summary.critical || 0} critical</span>
            {' · '}
            <span className="text-orange-600">{summary.high || 0} high</span>
            {' · '}
            <span className="text-amber-600">{summary.medium || 0} medium</span>
          </CardDescription>
        </div>
        <Button size="sm" variant="outline" onClick={() => runMut.mutate()}
          disabled={runMut.isPending}
          data-testid="copilot-briefing-rescan">
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${runMut.isPending ? 'animate-spin' : ''}`} />
          Rescan
        </Button>
      </CardHeader>
      <CardContent>
        {isLoading && <p className="text-xs text-slate-400">Loading…</p>}
        {!isLoading && findings.length === 0 && (
          <div className="flex items-center gap-2 text-emerald-700 text-sm py-2" data-testid="copilot-briefing-empty">
            <CheckCircle2 className="h-4 w-4" /> All systems healthy — no conflicts detected.
          </div>
        )}
        <div className="space-y-2 max-h-[420px] overflow-auto">
          {findings.map((f, i) => {
            const meta = SEVERITY_META[f.severity] || SEVERITY_META.low;
            const Icon = meta.icon;
            return (
              <div key={`${f.kind}-${i}`}
                className={`p-3 rounded border ${meta.color}`}
                data-testid={`copilot-briefing-finding-${f.kind}`}>
                <div className="flex items-start gap-2">
                  <Icon className="h-4 w-4 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium">{f.title}</p>
                      <Badge variant="outline" className="text-[9px] uppercase">{f.severity}</Badge>
                    </div>
                    <p className="text-xs mt-0.5 text-slate-700">{f.body}</p>
                    {f.fix_action && (
                      <p className="text-xs mt-1 italic text-slate-600">
                        <span className="font-semibold">Fix:</span> {f.fix_action}
                      </p>
                    )}
                    <button
                      onClick={() => {
                        const prompt = `Tell me more about the "${f.title}" finding (kind=${f.kind}, severity=${f.severity}) and recommend the safest next step. The suggested fix is: ${f.fix_action || '(none)'}.`;
                        window.dispatchEvent(new CustomEvent('copilot:open', {
                          detail: { prompt, autoSend: false },
                        }));
                      }}
                      data-testid={`copilot-briefing-ask-${f.kind}`}
                      className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-violet-700 hover:text-violet-900"
                    >
                      <MessageCircle className="h-3 w-3" /> Ask Copilot about this
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default CopilotBriefingWidget;
